/* hs-eslint ignored failing-rules */

/* eslint-disable no-bitwise */
'use es6';
/*
 * Selectors for widgets available to the active layout in the layout editor.
 *
 * The widgets themselves are sourced at:
 *  - CustomWidgets
 *  - GlobalGroups
 *  - GlobalWidgets
 */

import { createSelector } from 'reselect';
import SeamlessImmutable from 'seamless-immutable';
import { omit, once } from 'underscore';
import { AnonymousCriterionTypes } from 'smart-content-core/constants';
import * as WidgetCategories from 'DesignData/enums/WidgetCategories';
import { isCellType, isVirtualCellType } from 'DesignData/helpers/widgets';
import { EMPTY_OBJECT } from 'DesignData/lib/object';
import { getCustomWidgetsData, getCustomWidgetsOrder, getCustomWidgetsIsInitialized, getCustomWidgetsIsMappingInitialized, getCustomWidgetsByType } from 'DesignData/selectors/customWidgets';
import { makeGetMeta, getBodyEditable } from 'DesignData/selectors/layouts';
import { getShowLayoutSectionEditorOptions, getIsUngatedForContentTypes } from 'DesignData/selectors/gates';
import { ANY, PAGE, EMAIL, BLOG_POST, BLOG_LISTING, KNOWLEDGE_BASE, QUOTE } from 'DesignData/enums/CustomWidgetHostTemplateTypes';
import * as ModuleContentTypes from 'ContentUtils/constants/ModuleContentTypes';
/*************************************
 *
 * Widget Categories
 *
 *************************************/

/**
 * Returns an enum of WidgetCategories allowed for active layout.
 *
 * @return {Object.<DesignData/enums/WidgetCategories>}
 */

const makeGetWidgetCategories = () => {
  const getMeta = makeGetMeta();
  return createSelector([getMeta], meta => {
    const remove = [];

    if (meta.isGlobalGroup) {
      remove.push(WidgetCategories.GLOBAL_GROUP);
    }

    if (meta.isEmail) {
      remove.push(WidgetCategories.GLOBAL_GROUP);
    }

    const cats = omit(WidgetCategories, remove);
    return cats;
  });
};
/*************************************
 *
 * Custom Widgets
 *
 *************************************/


export const makeGetLayoutTemplateTypes = () => {
  const getMeta = makeGetMeta();
  return createSelector([getMeta, getIsUngatedForContentTypes], (meta, isUngatedForContentTypes) => {
    if (isUngatedForContentTypes) {
      let contentTypes = SeamlessImmutable({
        [ANY]: true
      });

      if (meta.isPage || meta.isSystemPage || meta.isSystemEmail) {
        contentTypes = contentTypes.merge({
          [ModuleContentTypes.LANDING_PAGE]: true,
          [ModuleContentTypes.SITE_PAGE]: true
        });
      }

      if (meta.isBlog) {
        contentTypes = contentTypes.merge({
          [ModuleContentTypes.BLOG_LISTING]: true,
          [ModuleContentTypes.BLOG_POST]: true
        });
      }

      if (meta.isEmail || meta.isSystemEmail) {
        contentTypes = contentTypes.set(ModuleContentTypes.EMAIL, true);
      }

      if (meta.isKnowledgeArticle) {
        contentTypes = contentTypes.set(ModuleContentTypes.KNOWLEDGE_BASE, true);
      }

      if (meta.isQuote) {
        contentTypes = contentTypes.set(ModuleContentTypes.QUOTE_TEMPLATE, true);
      }

      if (meta.isSystemEmail) {
        contentTypes = contentTypes.set(ModuleContentTypes.SUBSCRIPTION, true);
      }

      return contentTypes;
    }

    let hostTemplateTypes = SeamlessImmutable({
      [ANY]: true
    });

    if (meta.isPage || meta.isSystemPage || meta.isSystemEmail) {
      hostTemplateTypes = hostTemplateTypes.set(PAGE, true);
    }

    if (meta.isBlog) {
      hostTemplateTypes = hostTemplateTypes.merge({
        [BLOG_LISTING]: true,
        [BLOG_POST]: true
      });
    }

    if (meta.isEmail || meta.isSystemEmail) {
      hostTemplateTypes = hostTemplateTypes.set(EMAIL, true);
    }

    if (meta.isKnowledgeArticle) {
      hostTemplateTypes = hostTemplateTypes.set(KNOWLEDGE_BASE, true);
    }

    if (meta.isQuote) {
      hostTemplateTypes = hostTemplateTypes.set(QUOTE, true);
    }

    return hostTemplateTypes;
  });
};
/**
 * Custom Widgets by id and their order.
 */

const makeGetCustomWidgets = ({
  includeGlobal = true
} = {}) => {
  let customWidgets = EMPTY_OBJECT;
  const getLayoutTemplateTypes = makeGetLayoutTemplateTypes();
  return createSelector([getCustomWidgetsOrder, getCustomWidgetsData, getLayoutTemplateTypes, getIsUngatedForContentTypes], (order, widgets, layoutTemplateTypes, isUngatedForContentTypes) => {
    const __order = [];
    const __widgets = {};
    order.forEach(id => {
      const widget = widgets[id];

      if ((!widget.global || includeGlobal) && !widget.default && !widget.isThemeAsset && (isUngatedForContentTypes ? widget.contentTypes || [] : widget.hostTemplateTypes || []).some(templateType => layoutTemplateTypes[templateType])) {
        __widgets[id] = widget;

        __order.push(id);
      }
    });
    customWidgets = customWidgets.merge({
      order: __order,
      widgets: __widgets
    });
    return customWidgets;
  });
};
/*************************************
 *
 * Default widgets
 *
 *************************************/


const createMakeGetWidgetsByCondition = condition => {
  return () => {
    let defaultWidgets = EMPTY_OBJECT;
    const getLayoutTemplateTypes = makeGetLayoutTemplateTypes();
    return createSelector([getCustomWidgetsOrder, getCustomWidgetsData, getLayoutTemplateTypes, getCustomWidgetsByType, getIsUngatedForContentTypes], (order, widgets, layoutTemplateTypes, customWidgetsByType, isUngatedForContentTypes) => {
      const __order = [];
      const __widgets = {};
      const {
        horizontal_spacer
      } = customWidgetsByType;
      order.forEach(id => {
        const widget = widgets[id];

        if (condition(widget) && widget.schemaVersion > 1 && widget !== horizontal_spacer && // https://git.hubteam.com/HubSpot/DesignManagerUI/issues/2401
        (isUngatedForContentTypes ? widget.contentTypes || [] : widget.hostTemplateTypes || []).some(templateType => layoutTemplateTypes[templateType])) {
          __widgets[id] = widget;

          __order.push(id);
        }
      });
      defaultWidgets = defaultWidgets.merge({
        order: __order,
        widgets: __widgets
      });
      return defaultWidgets;
    });
  };
};

const makeGetDefaultWidgets = createMakeGetWidgetsByCondition(widget => {
  return widget.default && !widget.isThemeAsset;
});
const makeGetDefaultWidgetsAvailableForNewContent = createMakeGetWidgetsByCondition(widget => {
  return widget.default && !widget.isThemeAsset && widget.isAvailableForNewContent;
});
const makeGetThemeWidgets = createMakeGetWidgetsByCondition(widget => {
  return widget.isThemeAsset;
});
/*************************************
 *
 * Global Groups
 *
 *************************************/

const getGlobalGroupsOrder = state => state.globalGroups.widgets.order;

const getGlobalGroupsData = state => state.globalGroups.widgets.widgets;

const getGlobalGroupsIsInitialized = state => state.globalGroups.widgets.isInitialized;
/**
 * Global Groups by id and their order.
 */


const makeGetGlobalGroups = once(() => {
  let globalGroups = EMPTY_OBJECT;
  return createSelector([getGlobalGroupsOrder, getGlobalGroupsData], (order, widgets) => {
    globalGroups = globalGroups.merge({
      order,
      widgets
    });
    return globalGroups;
  });
});
/*************************************
 *
 * Global Widgets
 *
 *************************************/

const getGlobalWidgetsSummaryOrder = state => state.globalWidgets.summaries.order;

const getGlobalWidgetsSummaryData = state => state.globalWidgets.summaries.widgets;

const getGlobalWidgetsSummaryIsInitialized = state => state.globalWidgets.summaries.isInitialized;

const makeGetGlobalCustomWidgets = () => {
  let customWidgets = EMPTY_OBJECT;
  const getLayoutTemplateTypes = makeGetLayoutTemplateTypes();
  return createSelector([getCustomWidgetsOrder, getCustomWidgetsData, getLayoutTemplateTypes, getIsUngatedForContentTypes], (widgetsOrder, widgets, layoutTemplateTypes, isUngatedForContentTypes) => {
    const order = [];
    const byId = {};
    const byName = {};
    widgetsOrder.forEach(id => {
      const widget = widgets[id];

      if (widget.global && !widget.isThemeAsset && (isUngatedForContentTypes ? widget.contentTypes || [] : widget.hostTemplateTypes || []).some(templateType => layoutTemplateTypes[templateType])) {
        byId[id] = widget;
        byName[widget.name] = widget;
        order.push(id);
      }
    });
    customWidgets = customWidgets.merge({
      order,
      widgets: byId,
      widgetsByName: byName
    });
    return customWidgets;
  });
};
/**
 * Global Widget Summaries allowable for layout.
 */


export const makeGetGlobalWidgetSummaries = () => {
  const getMeta = makeGetMeta();
  const getGlobalCustomWidgets = makeGetGlobalCustomWidgets();
  return createSelector([getMeta, getGlobalWidgetsSummaryOrder, getGlobalWidgetsSummaryData, getGlobalCustomWidgets], (meta, widgetsOrder, widgetsById, globalCustomWidgets) => {
    const {
      isEmail
    } = meta;
    const order = [];
    const byId = {};
    const byName = {};

    for (const widgetId of widgetsOrder) {
      const widget = widgetsById[widgetId];
      const {
        smart_type: smartType
      } = widget.body;

      if (!(isEmail && smartType && AnonymousCriterionTypes[smartType])) {
        order.push(widgetId);
        byId[widgetId] = widget;
        byName[widget.name] = widget;
      }
    }

    const globalWidgets = SeamlessImmutable({
      order: globalCustomWidgets.order.concat(order),
      widgets: globalCustomWidgets.widgets.merge(byId),
      widgetsByName: globalCustomWidgets.widgetsByName.merge(byName)
    });
    return globalWidgets;
  });
};
/*************************************
 *
 * Structural Widgets
 *
 *************************************/

const getStructuralWidgetsOrder = state => state.structuralWidgets.order;

export const getStructuralWidgetsByType = state => state.structuralWidgets.widgets;
/**
 * Structural Widgets by id and their order.
 */

export const makeGetStructuralWidgets = () => {
  const getMeta = makeGetMeta();
  let result = SeamlessImmutable({
    order: [],
    widgets: {}
  });
  return createSelector([getMeta, getStructuralWidgetsOrder, getStructuralWidgetsByType, getCustomWidgetsByType, getShowLayoutSectionEditorOptions, getBodyEditable], (meta, structuralOrder, structuralWidgets, customWidgetsByType, showLayoutSectionEditorOptions, bodyEditable) => {
    const {
      categoryId,
      templateType
    } = meta || {};
    const order = [];
    const widgets = {};
    structuralOrder.forEach(type => {
      // Filter out virtual_cell
      if (isVirtualCellType(type) && (!showLayoutSectionEditorOptions || meta.isVirtualBodyCapable && bodyEditable)) {
        return;
      } // Filter by category


      const widget = structuralWidgets[type];
      const {
        hiddenFromCategories,
        hiddenFromTemplateTypes
      } = widget;

      if (hiddenFromCategories && !!~hiddenFromCategories.indexOf(categoryId)) {
        return;
      }

      if (hiddenFromTemplateTypes && !!~hiddenFromTemplateTypes.indexOf(templateType)) {
        return;
      }

      order.push(type);
      widgets[type] = widget;
    }); // https://git.hubteam.com/HubSpot/DesignManagerUI/issues/2401

    order.push('horizontal_spacer');
    widgets.horizontal_spacer = customWidgetsByType.horizontal_spacer;
    result = result.merge({
      order,
      widgets
    }, {
      deep: true
    });
    return result;
  });
};
/*************************************
 *
 * Widget Catalog
 *
 *************************************/

/**
 * @return {Object} Catalog of all available widgets by available categories.
 */

export const makeGetWidgetCatalog = () => {
  const getWidgetCategories = makeGetWidgetCategories();
  const getCustomWidgets = makeGetCustomWidgets({
    includeGlobal: false
  });
  const getGlobalGroups = makeGetGlobalGroups();
  const getGlobalWidgetSummaries = makeGetGlobalWidgetSummaries();
  const getStructuralWidgets = makeGetStructuralWidgets();
  const getDefaultWidgets = makeGetDefaultWidgetsAvailableForNewContent();
  const getThemeWidgets = makeGetThemeWidgets();
  let catalog = EMPTY_OBJECT;
  return createSelector([getWidgetCategories, getCustomWidgets, getGlobalGroups, getGlobalWidgetSummaries, getStructuralWidgets, getDefaultWidgets, getThemeWidgets], (widgetCategories, customWidgets, globalGroups, globalWidgets, structuralWidgets, defaultWidgets, themeWidgets) => {
    catalog = widgetCategories[WidgetCategories.CUSTOM_WIDGET] ? catalog.merge({
      customWidgets
    }) : catalog.without('customWidgets');
    catalog = widgetCategories[WidgetCategories.GLOBAL_GROUP] ? catalog.merge({
      globalGroups
    }) : catalog.without('globalGroups');
    catalog = widgetCategories[WidgetCategories.GLOBAL_WIDGET] ? catalog.merge({
      globalWidgets
    }) : catalog.without('globalWidgets');
    catalog = widgetCategories[WidgetCategories.STRUCTURAL_WIDGET] ? catalog.merge({
      structuralWidgets
    }) : catalog.without('structuralWidgets');
    catalog = widgetCategories[WidgetCategories.DEFAULT_WIDGET] ? catalog.merge({
      defaultWidgets
    }) : catalog.without('defaultWidgets');
    catalog = widgetCategories[WidgetCategories.THEME_WIDGET] ? catalog.merge({
      themeWidgets
    }) : catalog.without('themeWidgets');
    return catalog;
  });
};

const getLayoutId = (__, {
  layoutId,
  assetId
}) => layoutId || assetId;

const getRevisionId = (__, {
  revisionId
}) => revisionId;
/**
 * Gets a widget catalog selector memoized to
 *  props.layoutId || props.assetId
 *  props.revisionId
 *
 * This is so there is just a single catalog selector for all of
 * a layout's components.
 */


export const getWidgetCatalogSelectorByLayout = createSelector([getLayoutId, getRevisionId], // eslint-disable-next-line no-unused-vars
(layoutId, revisionId) => {
  return makeGetWidgetCatalog();
});
/**
 * @return {Boolean} Boolean flag representing widget catalog initialization.
 */

export const getWidgetCatalogIsInitialized = createSelector([getCustomWidgetsIsInitialized, getCustomWidgetsIsMappingInitialized, getGlobalGroupsIsInitialized, getGlobalWidgetsSummaryIsInitialized], (customWidgetsIsInitialized, customWidgetsIsMappingInitialized, globalGroupsIsInitialized, globalWidgetsIsInitialized) => {
  return customWidgetsIsInitialized && customWidgetsIsMappingInitialized && globalGroupsIsInitialized && globalWidgetsIsInitialized;
});
/**
 * Specific catalogue to adding widgets from finder
 */

export const makeGetFinderWidgetCatalog = () => {
  const getWidgetCategories = makeGetWidgetCategories();
  const getCustomWidgets = makeGetCustomWidgets({
    includeGlobal: true
  });
  const getGlobalGroups = makeGetGlobalGroups();
  const getDefaultWidgets = makeGetDefaultWidgets();
  const getThemeWidgets = makeGetThemeWidgets();
  let catalog = EMPTY_OBJECT;
  return createSelector([getWidgetCategories, getCustomWidgets, getGlobalGroups, getDefaultWidgets, getThemeWidgets], (widgetCategories, customWidgets, globalGroups, defaultWidgets, themeWidgets) => {
    catalog = widgetCategories[WidgetCategories.CUSTOM_WIDGET] ? catalog.merge(Object.assign({}, customWidgets.widgets)) : catalog.without(Object.keys(customWidgets.widgets));
    catalog = widgetCategories[WidgetCategories.GLOBAL_GROUP] ? catalog.merge(Object.assign({}, globalGroups.widgets)) : catalog.without(Object.keys(globalGroups.widgets));
    catalog = widgetCategories[WidgetCategories.DEFAULT_WIDGET] ? catalog.merge(Object.assign({}, defaultWidgets.widgets)) : catalog.without(Object.keys(defaultWidgets.widgets));
    catalog = widgetCategories[WidgetCategories.THEME_WIDGET] ? catalog.merge(Object.assign({}, themeWidgets.widgets)) : catalog.without(Object.keys(themeWidgets.widgets));
    return catalog;
  });
};
/*************************************
 *
 * Builtins by type
 *
 *************************************/

/**
 * Helper function.
 * Direct state lookup of builtin widgets.
 *
 *  IDE  -> structuralWidgets || CMv2
 *
 * @param {Object} state
 * @param {String} type
 * @return {(DesignData/models/widgets/Widget|Null)}
 */

export function getBuiltinByType(state, type) {
  if (isCellType(type)) {
    const structuralWidgetsByType = getStructuralWidgetsByType(state);
    return structuralWidgetsByType[type] || null;
  }

  const customWidgetsByType = getCustomWidgetsByType(state);
  return customWidgetsByType[type] || null;
}